import type {
  Feature, Geometry, Polygon, MultiPolygon,
} from 'geojson';

type Point = [number, number, number]; // [lat, lng, value]

function pointInRing(lat: number, lng: number, ring: number[][]): boolean {
  let inside = false;
  let j = ring.length - 1;
  for (let i = 0; i < ring.length; i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
    j = i;
  }
  return inside;
}

function pointInPolygon(lat: number, lng: number, coords: number[][][]): boolean {
  if (!pointInRing(lat, lng, coords[0])) return false;
  for (let h = 1; h < coords.length; h += 1) {
    if (pointInRing(lat, lng, coords[h])) return false;
  }
  return true;
}

export function pointInFeature(lat: number, lng: number, feature: Feature): boolean {
  const { geometry } = feature;
  if (!geometry) return false;
  if ((geometry as Geometry).type === 'Polygon') {
    return pointInPolygon(lat, lng, (geometry as Polygon).coordinates);
  }
  if ((geometry as Geometry).type === 'MultiPolygon') {
    return (geometry as MultiPolygon).coordinates.some(
      (poly) => pointInPolygon(lat, lng, poly),
    );
  }
  return false;
}

export interface StateAggregate {
  avg: number;
  min: number;
  max: number;
  count: number;
}

export function aggregateForFeature(
  feature: Feature,
  points: Point[],
): StateAggregate | null {
  const inside = points.filter(([lat, lng]) => pointInFeature(lat, lng, feature));
  if (!inside.length) return null;
  const values = inside.map(([, , v]) => v);
  const sum = values.reduce((a, b) => a + b, 0);
  return {
    avg: Math.round(sum / values.length),
    min: Math.round(Math.min(...values)),
    max: Math.round(Math.max(...values)),
    count: inside.length,
  };
}

// Retorna o valor do ponto mais próximo ou null se além de maxDistDeg graus
// (~200 km com default 1.8). Evita tooltip sobre oceano.
export function findNearestValue(
  lat: number,
  lng: number,
  points: Point[],
  maxDistDeg = 1.8,
): number | null {
  if (!points.length) return null;
  const maxDistSq = maxDistDeg ** 2;
  let minDistSq = Infinity;
  let nearest: number | null = null;
  points.forEach(([plat, plng, val]) => {
    const distSq = (lat - plat) ** 2 + (lng - plng) ** 2;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearest = val;
    }
  });
  return minDistSq <= maxDistSq ? nearest : null;
}
